import { ArrowRight, Pause, Play, Square, Timer } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../../../client/components/ui/button";
import { Input } from "../../../client/components/ui/input";
import { Switch } from "../../../client/components/ui/switch";
import useTranslation from "../../../client/hooks/useTranslation";
import { formatTimerTime, useSessionTimer } from "../../../trading-sessions/useSessionTimer";
import { net } from "../../lib/stats";
import type { LiteTrade } from "../../lib/types";
import { Pnl } from "../../ui/ui";
import { LiveDot, MoodChip, useDurationLabel } from "./SessionBits";

/**
 * Session minutée : chrono en cours (pause, arrêt + évaluation) ou lancement.
 * Toute la logique vient de useSessionTimer, la même que le widget « Session »
 * de l'Advanced ; seul le rendu change.
 */
export function LiveSession({ trades, currentSessionId, hideIdle = false }: {
  trades: LiteTrade[];
  /** Page de détail : pas de lien vers la session qu'on regarde déjà. */
  currentSessionId?: string;
  /** Détail d'une autre session : rien à afficher quand aucune session ne tourne. */
  hideIdle?: boolean;
}) {
  const t = useTranslation();
  const ts = t.lite.sessions;
  const timer = useSessionTimer();
  const { activeSession, isLoading, hasTimer, timeRemaining, elapsedSeconds, isPaused } = timer;
  const duration = useDurationLabel();

  if (isLoading) return null;
  const live = activeSession && activeSession.isActive ? activeSession : null;
  if (!live && hideIdle) return null;
  if (live && currentSessionId && live.id !== currentSessionId && hideIdle) return null;

  if (live) {
    // Trades entrés depuis le lancement : le résultat de la session en direct.
    const startMs = live.startedAt ? new Date(live.startedAt).getTime() : Date.now();
    const since = trades.filter(tr => new Date(tr.openedAt).getTime() >= startMs);
    const pnl = since.reduce((s, tr) => s + net(tr), 0);
    const clock = hasTimer ? (timeRemaining !== null ? formatTimerTime(timeRemaining) : "--:--") : formatTimerTime(elapsedSeconds);
    const clockLabel = isPaused ? t.session.paused : hasTimer ? t.session.remaining : t.session.elapsed;
    return (
      <>
        <div className="flex flex-col lg:flex-row lg:items-center gap-5 lg:gap-8">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-sm font-medium text-foreground">
              <LiveDot />{ts.live.badge}
              {(live as any).name && <span className="text-muted-foreground font-normal truncate">· {(live as any).name}</span>}
            </div>
            <div className="mt-2 flex items-baseline gap-3 flex-wrap">
              <span className={`font-mono tabular-nums text-4xl md:text-5xl font-semibold tracking-tight ${isPaused ? "text-muted-foreground" : "text-primary"}`}>{clock}</span>
              <span className="text-sm text-muted-foreground">{clockLabel}</span>
            </div>
            <div className="mt-2 flex items-center gap-2 flex-wrap text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1"><Timer size={13} />{live.plannedDuration != null ? ts.live.planned(duration(live.plannedDuration)) : ts.live.noTimer}</span>
              {(live as any).mood && <><span>·</span><span>{ts.live.startMood}</span><MoodChip mood={(live as any).mood} /></>}
            </div>
          </div>

          <div className="lg:border-l lg:border-border lg:pl-8">
            <div className="text-xs text-muted-foreground">{ts.live.sinceStart}</div>
            <Pnl value={pnl} decimals className="text-2xl font-semibold mt-1 block" />
            <div className="text-xs text-muted-foreground mt-0.5">{t.lite.common.trades(since.length)}</div>
          </div>

          <div className="flex flex-wrap items-center gap-2 lg:ml-auto">
            <Button variant="outline" onClick={timer.handlePauseResume}>
              {isPaused ? <Play className="h-4 w-4 mr-1.5" /> : <Pause className="h-4 w-4 mr-1.5" />}
              {isPaused ? t.session.resume : t.session.pause}
            </Button>
            <Button variant="destructive" onClick={timer.handleStopSession}>
              <Square className="h-4 w-4 mr-1.5" />{t.session.stop}
            </Button>
            {live.id !== currentSessionId && (
              <Button variant="ghost" asChild>
                <Link to={`/session/${live.id}`}>{ts.live.open}<ArrowRight className="h-4 w-4 ml-1.5" /></Link>
              </Button>
            )}
          </div>
        </div>
        {timer.dialogs}
      </>
    );
  }

  return (
    <>
      <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-8">
        <div className="min-w-0 flex-1">
          <h2 className="font-display text-lg font-bold">{ts.idle.title}</h2>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <label className="inline-flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
            <Switch checked={timer.noTimer} onCheckedChange={timer.setNoTimer} aria-label={t.session.noTimerLabel} />
            {t.session.noTimerLabel}
          </label>
          {!timer.noTimer && (
            <label className="inline-flex items-center gap-2 text-sm text-muted-foreground">
              <span className="sr-only">{ts.idle.duration}</span>
              <Input
                type="number"
                min="1"
                value={timer.plannedDuration}
                onChange={e => timer.setPlannedDuration(parseInt(e.target.value) || 120)}
                className="h-9 w-20 font-mono"
                aria-label={ts.idle.duration}
                title={ts.idle.duration}
              />
              {ts.idle.minutes}
            </label>
          )}
          <Button onClick={timer.handleOpenStartDialog}>
            <Play className="h-4 w-4 mr-1.5" />{t.session.start}
          </Button>
        </div>
      </div>
      {timer.dialogs}
    </>
  );
}
